import { DragDropProvider } from '@dnd-kit/react'
import { isSortable, useSortable } from '@dnd-kit/react/sortable'
import { Minus } from 'lucide-react'
import { moveTile, setTileShown } from '@/lib/homeLayouts'
import { cn } from '@/lib/utils'
import { CARD_SHELL, CardBody, type HomeNavItem } from './HomeTile'

/**
 * Edit mode: the layout's tiles wiggle, a "−" hides one, and dragging moves one
 * while the others slide out of the way. Keyboard users focus a tile, press
 * Space, move it with the arrow keys and press Space again (dnd-kit's keyboard
 * sensor); Choose apps also has up and down buttons.
 */
export function HomeEditGrid({
  items,
  draft,
  onChange,
}: {
  items: HomeNavItem[]
  draft: string[]
  onChange: (draft: string[]) => void
}) {
  return (
    <DragDropProvider
      onDragEnd={event => {
        if (event.canceled) return
        const { source } = event.operation
        if (!isSortable(source)) return
        onChange(moveTile(draft, source.initialIndex, source.index))
      }}
    >
      <div className='grid grid-cols-3 gap-2'>
        {items.map((item, index) => (
          <EditTile
            key={item.id}
            item={item}
            index={index}
            onHide={() => onChange(setTileShown(draft, item.id, false))}
          />
        ))}
      </div>
    </DragDropProvider>
  )
}

function EditTile({ item, index, onHide }: { item: HomeNavItem; index: number; onHide: () => void }) {
  const { ref, handleRef, isDragging } = useSortable({ id: item.id, index })
  return (
    <div ref={ref} className={cn('relative', isDragging && 'z-10')}>
      {/* The handle is the card, not the wrapper, so pressing "−" never starts a drag. */}
      <div
        ref={handleRef}
        tabIndex={0}
        aria-label={`${item.label}. Press Space to move`}
        aria-roledescription='movable app'
        className={cn(
          CARD_SHELL,
          'cursor-grab select-none touch-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          isDragging ? 'cursor-grabbing opacity-80 shadow-lg' : 'motion-safe:animate-wiggle',
        )}
        // Neighbours start half a beat apart, so the grid does not swing as one.
        style={{ animationDelay: index % 2 ? '-0.15s' : undefined }}
      >
        <CardBody item={item} badge={0} disabled={item.disabled} underline={false} />
      </div>
      <button
        type='button'
        onClick={onHide}
        aria-label={`Hide ${item.label}`}
        title={`Hide ${item.label}`}
        className='absolute -left-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-gray-600 text-white shadow ring-2 ring-white hover:bg-gray-800'
      >
        <Minus className='h-3 w-3' />
      </button>
    </div>
  )
}
